import { useState, useEffect } from 'react';
import {
  Pencil, MoreHorizontal, ShieldCheck, ShieldOff, Globe, Monitor, KeyRound, UserX, UserCheck, Trash2, Plus, X,
  Lock, CircleSlash, Loader2, Activity,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { UserAvatar, displayName, formatRelative, formatDate } from './common';

const TABS = [{ id: 'info', label: 'Genel bilgiler' }, { id: 'security', label: 'Güvenlik' }, { id: 'sessions', label: 'Oturumlar' }];

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="text-right min-w-0 break-words">{children}</span>
  </div>
);

const Block = ({ title, children }) => (
  <section className="py-4 border-b border-border last:border-0">
    <h3 className="font-semibold mb-2">{title}</h3>
    {children}
  </section>
);

const SslBadge = ({ port }) => (
  [993, 465].includes(Number(port))
    ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 px-1.5 py-0.5 text-[11px] font-semibold"><Lock className="h-3 w-3" />SSL</span>
    : null
);

export const UserDetailPanel = ({ details, loading, onClose, actions }) => {
  const [tab, setTab] = useState('info');
  const user = details?.user;
  useEffect(() => { setTab('info'); }, [user?.id]);

  if (loading || !user) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[300px]">
        {loading ? <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /> : <p className="text-sm text-muted-foreground">Kullanıcı bulunamadı</p>}
      </div>
    );
  }

  const sessions = details.active_sessions || [];
  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="user-detail">
      <div className="p-5 pb-3 flex items-start gap-3">
        <UserAvatar user={user} size="h-12 w-12" text="text-base" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold truncate" data-testid="detail-name">{displayName(user)}</h2>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${user.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' : 'bg-secondary text-muted-foreground'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${user.is_active ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
              {user.is_active ? 'Aktif' : 'Askıda'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
        </div>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => actions.onEdit(user)} aria-label="Düzenle" data-testid="detail-edit"><Pencil className="h-4 w-4" /></Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" aria-label="Diğer işlemler" data-testid="detail-more"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => actions.onReset(user)}><KeyRound className="h-4 w-4 mr-2" />Şifre sıfırla</DropdownMenuItem>
            <DropdownMenuItem onClick={() => actions.onToggleStatus(user)}>
              {user.is_active ? <UserX className="h-4 w-4 mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
              {user.is_active ? 'Hesabı askıya al' : 'Hesabı etkinleştir'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => actions.onCpanelTest(user)}><Activity className="h-4 w-4 mr-2" />cPanel bağlantısını test et</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => actions.onDelete(user)} className="text-destructive" data-testid="detail-delete"><Trash2 className="h-4 w-4 mr-2" />Kullanıcıyı sil</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {onClose && <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 xl:hidden" onClick={onClose} aria-label="Paneli kapat" data-testid="detail-close"><X className="h-4 w-4" /></Button>}
      </div>

      <div className="flex gap-6 px-5 border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            data-testid={`detail-tab-${t.id}`}
            className={`py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t.label}
            {t.id === 'sessions' && <span className="ml-2 rounded-full bg-secondary px-1.5 py-0.5 text-xs text-foreground">{sessions.length}</span>}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5">
        {tab === 'info' && (
          <>
            <Block title="Hesap bilgileri">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><p className="text-muted-foreground">Ad soyad</p><p className="font-medium">{displayName(user)}</p></div>
                <div><p className="text-muted-foreground">Son giriş</p><p className="font-medium">{formatRelative(user.last_login)}</p></div>
                <div><p className="text-muted-foreground">Kayıt tarihi</p><p className="font-medium">{user.created_at ? new Date(user.created_at).toLocaleDateString('tr-TR') : '-'}</p></div>
                <div><p className="text-muted-foreground">Dil / Tema</p><p className="font-medium">{user.language === 'tr' ? 'Türkçe' : user.language} / {user.theme === 'dark' ? 'Koyu' : 'Açık'}</p></div>
                <div className="col-span-2"><p className="text-muted-foreground">Toplu mail</p><p className="font-medium">{user.bulk_mail_enabled ? `Açık (günlük ${user.bulk_mail_daily_limit ?? '-'} / saatlik ${user.bulk_mail_hourly_limit ?? '-'})` : 'Kapalı'}</p></div>
              </div>
            </Block>
            <Block title="Erişim güvenliği">
              <Row label="İki adımlı doğrulama">
                {user.two_fa_enabled ? <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><ShieldCheck className="h-4 w-4" />Etkin</span> : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><ShieldOff className="h-4 w-4" />Kapalı</span>}
              </Row>
              <Row label="IP kısıtlaması">
                {user.ip_restriction_enabled ? <span className="inline-flex items-center gap-1.5 text-amber-600"><Globe className="h-4 w-4" />Etkin ({details.allowed_ips?.length || 0} IP)</span> : <span className="inline-flex items-center gap-1.5 text-muted-foreground"><CircleSlash className="h-4 w-4" />Tanımlı değil</span>}
              </Row>
              <Row label="Aktif oturumlar">
                <span className="inline-flex items-center gap-2"><Monitor className="h-4 w-4 text-muted-foreground" />{sessions.length} cihaz
                  <button className="text-primary hover:underline" onClick={() => setTab('sessions')}>Görüntüle</button></span>
              </Row>
            </Block>
            <Block title="Posta sunucusu">
              <Row label="IMAP"><span className="inline-flex items-center gap-2">{user.imap_host} : {user.imap_port} <SslBadge port={user.imap_port} /></span></Row>
              <Row label="SMTP"><span className="inline-flex items-center gap-2">{user.smtp_host} : {user.smtp_port} <SslBadge port={user.smtp_port} /></span></Row>
            </Block>
          </>
        )}

        {tab === 'security' && (
          <div className="py-4 space-y-4">
            <section className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />İki faktörlü doğrulama</h3>
                {user.two_fa_enabled && <Button variant="outline" size="sm" className="text-destructive" onClick={() => actions.onDisable2FA(user)} data-testid="disable-2fa"><ShieldOff className="h-4 w-4 mr-1" />Devre dışı bırak</Button>}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {user.two_fa_enabled
                  ? `Etkin — yöntem: ${user.two_fa_method === 'sms' ? 'SMS' : 'Authenticator'}${user.phone_number ? ` (${user.phone_number})` : ''}`
                  : '2FA etkin değil'}
              </p>
            </section>
            <section className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-semibold flex items-center gap-2"><Globe className="h-4 w-4 text-primary" />IP kısıtlaması</h3>
                <div className="flex items-center gap-2">
                  <Switch checked={!!user.ip_restriction_enabled} onCheckedChange={() => actions.onToggleIP(user)} aria-label="IP kısıtlaması" data-testid="toggle-ip-restriction" />
                  {user.ip_restriction_enabled && <Button variant="outline" size="sm" onClick={() => actions.onAddIP(user)} data-testid="add-user-ip"><Plus className="h-4 w-4 mr-1" />IP ekle</Button>}
                </div>
              </div>
              {user.ip_restriction_enabled ? (
                <ul className="mt-3 space-y-2">
                  {(details.allowed_ips || []).length === 0 && <li className="text-sm text-muted-foreground">Henüz IP adresi eklenmemiş</li>}
                  {(details.allowed_ips || []).map((ip) => (
                    <li key={ip.id} className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2">
                      <code className="text-sm">{ip.ip_address}</code>
                      <button className="text-destructive p-1 rounded hover:bg-destructive/10" onClick={() => actions.onRemoveIP(user, ip)} aria-label={`${ip.ip_address} kaldır`}><X className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-muted-foreground mt-2">IP kısıtlaması etkin değil</p>}
            </section>
          </div>
        )}

        {tab === 'sessions' && (
          <div className="py-4 space-y-3">
            {sessions.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Aktif oturum yok</p>}
            {sessions.map((s) => (
              <div key={s.id} className="rounded-xl border border-border p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-mono">{s.ip_address}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.user_agent || 'Bilinmeyen cihaz'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Oluşturulma: {formatDate(s.created_at)}</p>
                </div>
                <Button variant="outline" size="sm" className="text-destructive shrink-0" onClick={() => actions.onInvalidateSession(user, s)}>Sonlandır</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => actions.onReset(user)} data-testid="detail-reset"><KeyRound className="h-4 w-4 mr-2" />Şifre sıfırla</Button>
        <Button variant="outline" size="sm" onClick={() => actions.onInvalidateAll(user)} disabled={sessions.length === 0} data-testid="detail-end-sessions"><Monitor className="h-4 w-4 mr-2" />Oturumları sonlandır</Button>
        <Button variant="ghost" size="sm" className={`ml-auto ${user.is_active ? 'text-destructive hover:text-destructive' : 'text-emerald-600'}`} onClick={() => actions.onToggleStatus(user)} data-testid="detail-toggle-status">
          {user.is_active ? <><UserX className="h-4 w-4 mr-2" />Hesabı askıya al</> : <><UserCheck className="h-4 w-4 mr-2" />Hesabı etkinleştir</>}
        </Button>
      </div>
    </div>
  );
};

export default UserDetailPanel;
